import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { nip19 } from 'nostr-tools';
import { Badge } from './ui/badge';
import { useAuthor } from '@/hooks/useAuthor';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { genUserName } from '@/lib/genUserName';
import { Skeleton } from './ui/skeleton';
import { DeleteListButton } from './DeleteListButton';
import { kebabToTitleCase } from '@/lib/utils';
import { Deck, isFormatTag } from '@/lib/nostr/deck';
import { DeckCardBanner } from './DeckCardBanner';
import { Button } from './ui/button';
import { Share2, EyeOff, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface DeckCardProps {
  deck: Deck;
  variant?: 'default' | 'profile';
  onShare?: (deck: Deck) => void;
}

export const DeckCard = ({ deck, variant = 'default', onShare }: DeckCardProps) => {
  const { event, title } = deck;
  const { user } = useCurrentUser();
  const formats = event.tags.filter(isFormatTag).map(t => t[1]);
  const isUnlisted = event.tags.some(t => t[0] === 't' && t[1] === 'unlisted');
  const image = event.tags.find(t => t[0] === 'banner')?.[1];
  const { data: author, isLoading: isLoadingAuthor } = useAuthor(event.pubkey);
  const naddr = nip19.naddrEncode({
    identifier: event.tags.find(t => t[0] === 'd')?.[1] || '',
    pubkey: event.pubkey,
    kind: event.kind,
  });
  const listUrl = `/list/${naddr}`;

  return (
    <Card className="overflow-hidden shadow-lg shadow-black/20 flex flex-col relative">
      {isUnlisted && (
        <div className="absolute top-2 left-2 bg-background/80 rounded-full p-1.5 z-10">
          <EyeOff className="h-4 w-4 text-foreground" />
        </div>
      )}
      <CardHeader className="p-0">
        <DeckCardBanner imageUrl={image} listUrl={listUrl} />
      </CardHeader>
      <CardContent className="p-4 flex-grow">
        <CardTitle className="text-lg line-clamp-2 font-semibold">{title || 'Untitled Deck'}</CardTitle>
      </CardContent>
      <CardFooter className="p-4 pt-0">
        {variant === 'default' ? (
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {isLoadingAuthor ? (
                  <Skeleton className="h-6 w-24" />
                ) : (
                  <Link to={`/profile/${nip19.npubEncode(event.pubkey)}`} className="flex items-center gap-2 group">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={author?.metadata?.picture} />
                      <AvatarFallback>
                        <User className="h-3 w-3" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-muted-foreground group-hover:underline">{author?.metadata?.name || genUserName(event.pubkey)}</span>
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-2">
                {formats.map(format => (
                  <Badge key={format} variant="secondary">{kebabToTitleCase(format)}</Badge>
                ))}
              </div>
            </div>
            <DeleteListButton listEvent={event} />
          </div>
        ) : (
          <div className="w-full">
            <div className="flex justify-between items-center mb-4">
              <p className="text-muted-foreground text-sm">
                {deck.cards.reduce((acc, card) => acc + card.quantity, 0)} cards
              </p>
              <div className="flex gap-2">
                {formats.map(format => (
                  <Badge key={format} variant="secondary">{kebabToTitleCase(format)}</Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" className="flex-grow h-10">
                <Link to={listUrl}>
                  View Deck
                </Link>
              </Button>
              <Button variant="outline" size="icon" onClick={() => onShare?.(deck)}>
                <Share2 className="h-4 w-4" />
              </Button>
              {user?.pubkey === event.pubkey && (
                <DeleteListButton listEvent={event} />
              )}
            </div>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};
